/* import __COLOCATED_TEMPLATE__ from './asset-chip.hbs'; */
import { A } from '@ember/array';
import { alias, equal } from '@ember/object/computed';
import { computed } from '@ember/object';
import Component from '@ember/component';
import { showModal } from "sh-www/utils/modal-controls";
import splitFilename from '../utils/split-filename';

const CLASS_NAME = 'SH-c-asset-chip';

/** A component which renders a visual representation of an asset. */
export default Component.extend({
  /**
   * @inheritDoc
   */
  classNames: [CLASS_NAME],
  classNameBindings: [
    `isFluid:${CLASS_NAME}--is-fluid`,
    `hasDropdown:${CLASS_NAME}--has-dropdown`,
    `hasPadding:${CLASS_NAME}--has-padding`,
    `_isLayoutHorizontal:${CLASS_NAME}--is-horizontal:${CLASS_NAME}--is-vertical`,
  ],
  attributeBindings: ['filename:title'],

  CLASS_NAME,

  /*********************************
   * REQUIRED PARAMS
   *********************************/

  filename: null,

  /*********************************
   * OPTIONAL PARAMS
   *********************************/

  layout: 'horizontal',
  type: null,
  status: null,
  uploadID: null,
  progress: null,
  thumbnail: null,
  uriCollection: null,
  isFluid: false, // Whether to render full width with wrapping text vs constrained with truncation.
  hasDropdown: false,
  hasPadding: true,
  hasProgressBar: false,
  hasLargeText: false,
  hasFixedWidthThumbnails: false,
  hasDeleteButton: false,
  handleClickDeleteButton: () => (function() {}),

  /*********************************
   * PRIVATE
   *********************************/

  _assetName: computed(
    '_name',
    '_willWrap',
    '_extension',
    function () {
      const extension = this._willWrap ? `.${this._extension}` : '';
      return `${this._name}${extension}`;
    }
  ),

  /**
   * @type {Boolean} Whether the layout is horizontal.
   */
  _isLayoutHorizontal: computed('layout', function () {
    return this.get('layout') === 'horizontal';
  }),

  /**
   * @type {Boolean} Whether the text will wrap.
   */
  _willWrap: alias('isFluid'),

  /**
   * @type {String} The asset filename, without the extension.
   */
  _name: computed('filename', function () {
    return splitFilename(this.get('filename')).name;
  }),

  /**
   * @type {String} The asset's extension, without the period.
   */
  _extension: computed('filename', function () {
    return splitFilename(this.get('filename')).extension;
  }),

  /**
   * @type {String} The asset's MIME type category.
   */
  _typeCategory: computed('type', function () {
    return this.get('type').split('/')[0];
  }),

  /**
   * @type {Boolean} Whether the asset is previewable in a modal.
   */
  _isPreviewable: computed('_typeCategory', '_displayURL', function () {
    const typeCategory = this.get('_typeCategory');

    if (typeCategory !== 'image' && typeCategory !== 'video') {
      return false;
    }

    const displayURL = this.get('_displayURL');

    if (displayURL === null || displayURL === undefined) {
      return false;
    }

    return true;
  }),

  /**
   * @type {String} The URL of the original asset.
   */
  _originalURL: alias('uriCollection.original'),

  /**
   * @type {String} The URL of the thumbnail asset.
   */
  _thumbnailURL: computed('thumbnail', 'uriCollection.images.image_medium', function () {
    const clientThumbnail = this.get('thumbnail');

    if (clientThumbnail) {
      return clientThumbnail;
    }

    const serverThumbnail = this.get('uriCollection.images.image_medium');

    if (serverThumbnail) {
      return serverThumbnail;
    }

    return '';
  }),

  /**
   * @type {String} The URL of the display asset.
   */
  _displayURL: computed('uriCollection', function () {
    const typeCategory = this.get('_typeCategory');

    if (typeCategory === 'image') {
      return this.get('uriCollection.images.image_large');
    }

    if (typeCategory === 'video') {
      return this.get('uriCollection.videos.video_large');
    }

    return null;
  }),

  /**
   * @type {Boolean} Whether the asset's status is 'active' rather than 'draft'.
   */
  _isActive: equal('status', 'active'),

  /**
   * @type {Boolean} Whether the progress bar should be shown.
   */
  _showProgressBar: computed('hasProgressBar', '_isActive', function () {
    return this.get('hasProgressBar') && !this.get('_isActive');
  }),

  /**
   * @type {Array} The options in the context menu.
   */
  _menuItems: computed('_originalURL', '_isPreviewable', function () {
    const menu = A();

    if (this.get('_originalURL')) {
      menu.pushObject({
        title: 'Download',
        onClick: this.get('_download').bind(this),
      });
    }

    if (this.get('_isPreviewable')) {
      menu.pushObject({
        title: 'Preview',
        onClick: this.get('_preview').bind(this),
      });
    }

    return menu;
  }),

  /**
   * Downloads the asset.
   */
  _download() {
    window.open(this.get('_originalURL'));
  },

  /**
   * Previews the asset.
   */
  _preview() {
    showModal(this, 'asset-preview', {
      typeCategory: this.get('_typeCategory'),
      url: this.get('_displayURL'),
      handleClickDownload: () => {
        this._download();
      },
    });
  },

  actions: {
    onClickDeleteButton() {
      this.get('handleClickDeleteButton')(this.get('uploadID'));
    },
  },
});
